// 使用者偏好（狀態選擇、平台、排序、顯示方式）。key 帶版本號，格式改變時用 migrate 轉換，
// 讓升級後使用者的設定不會遺失（設計稿 §18）。
import { PLATFORMS, type PlatformKey } from '@dayone/core/dictionaries';
import { type KeyValueStore, readJson, writeJson } from './storage.ts';

export const PREFS_KEY = 'dayone:v1:prefs';

export type StatusKey = 'up' | 'rel';
export type SortKey = 'date' | 'date-desc' | 'hype' | 'name';
export type ViewKey = 'scroll' | 'pages';
/** 版面：海報牆（預設）或清單（GDO-16） */
export type LayoutKey = 'grid' | 'list';
export const PAGE_SIZES = [25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

export interface Prefs {
  status: StatusKey[];
  platforms: PlatformKey[];
  sort: SortKey;
  view: ViewKey;
  pageSize: PageSize;
  layout: LayoutKey;
}

export const DEFAULT_PREFS: Prefs = {
  status: ['up'],
  platforms: [],
  sort: 'date',
  view: 'scroll',
  pageSize: 50,
  layout: 'grid',
};

const STATUS: readonly StatusKey[] = ['up', 'rel'];
const SORTS: readonly SortKey[] = ['date', 'date-desc', 'hype', 'name'];
const VIEWS: readonly ViewKey[] = ['scroll', 'pages'];
const LAYOUTS: readonly LayoutKey[] = ['grid', 'list'];
const PLATFORM_KEYS = new Set<string>(PLATFORMS.map((p) => p.key));

/** 逐欄位驗證：任何一個欄位壞掉只回到該欄位的預設值 */
export function sanitizePrefs(raw: unknown): Prefs {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const status = Array.isArray(r.status) ? r.status.filter((s): s is StatusKey => STATUS.includes(s)) : [];
  const platforms = Array.isArray(r.platforms)
    ? r.platforms.filter((p): p is PlatformKey => typeof p === 'string' && PLATFORM_KEYS.has(p))
    : [];
  return {
    status: status.length ? [...new Set(status)] : DEFAULT_PREFS.status,
    platforms: [...new Set(platforms)],
    sort: SORTS.includes(r.sort as SortKey) ? (r.sort as SortKey) : DEFAULT_PREFS.sort,
    view: VIEWS.includes(r.view as ViewKey) ? (r.view as ViewKey) : DEFAULT_PREFS.view,
    pageSize: PAGE_SIZES.includes(r.pageSize as PageSize) ? (r.pageSize as PageSize) : DEFAULT_PREFS.pageSize,
    // 舊版偏好沒有這個欄位：使用預設的海報牆
    layout: LAYOUTS.includes(r.layout as LayoutKey) ? (r.layout as LayoutKey) : DEFAULT_PREFS.layout,
  };
}

export function loadPrefs(store?: KeyValueStore | null): Prefs {
  return sanitizePrefs(readJson(PREFS_KEY, store));
}

export function savePrefs(prefs: Prefs, store?: KeyValueStore | null): boolean {
  return writeJson(PREFS_KEY, prefs, store);
}
